import {Box} from "@mui/system";
import React, {useEffect, useRef, useState} from "react";
import {useParams} from "react-router";
import {BreadcrumbsWrapper} from "../../../../components/common/breadcrumbs-component/BreadcrumbsWrapper";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {Document as IDocument} from "../../models/Document";
import {DocumentViewer, IDocumentViewerHandles} from "../document-viewer/DocumentViewer";
import {GET_DOCUMENTS_BY_ID} from "../graphql/queries/gqlGetDocumentById";
import {GET_PRESIGNED_URL} from "../graphql/queries/gqlGetPresignedUrl";
import {DocumentActionsCard, mailDocumentType} from "./DocumentActionsCard";
import {DocumentCard} from "./DocumentCard";
import {MetaCard} from "./MetaCard";
import {DocumentMeta} from "../../models/DocumentMeta";
import {DocumentValidation} from "../../models/DocumentValidation";
import {DocumentAudit} from "../../models/DocumentAudit";
import {translateFunction} from "../../../../localization/TranslationService";
import {
	Alert,
	Button,
	Chip,
	CircularProgress,
	IconButton, Tooltip
} from "@mui/material";
import {ValidationSeverity} from "../../models/ValidationSeverity";
import {AuditCard} from "./AuditCard";
import {LogCard} from "./LogCard";
import {Comment, Refresh} from "@mui/icons-material";
import {pageAuthorizedByGroup} from "../../../fragments/PageAuthorization";
import {GenericModal} from "../../../querytool/components/generic-modal/GenericModal";
import {MailDocumentCard} from "../mail/mailDocumentCard";
import {SplitDocumentCard} from "../split/splitDocumentCard";
import {DocumentStatus} from "../../models/DocumentStatus";
import {LOCK_DOCUMENT} from "../graphql/subscriptions/gqlSubscribeLockDocument";
import {useSubscription} from "@apollo/client";
import {EditCommentCard} from "./EditCommentCard";
import {useNavigate} from "react-router-dom";
import {DocumentFileType} from "../../models/DocumentFileType";
import {ReindexDocumentCard} from "../reindex/reindexDocumentCard";
import {DocumentFile} from "../../models/DocumentFile";
import {SUBSCRIBE_SAVEMETADATA} from "../graphql/subscriptions/gqlSubscribeSaveMetaData";
import {toast} from "react-toastify";

const translate = translateFunction("SureBaseDocuments");

const containerStyling: React.CSSProperties = {
	display: "flex",
	flexDirection: "row",
	width: "100%",
	height: "100%",
	maxHeight: "100%",
};

const topContainerStyling: React.CSSProperties = {
	display: "flex",
	flexDirection: "row",
	width: "100%",
};


const metaDataContainerStyling: React.CSSProperties = {
	flexGrow: 3,
	width: "1px",
	overflow: "scroll",
};

const documentViewerContainerStyling: React.CSSProperties = {
	width: "1px",
	height: "100%",
	maxHeight: "100%",
	position: "relative",
	flexGrow: 7,
};

export async function getPresignedUrl(fileId: number, documentId: number, text: boolean): Promise<{url: string; fileName: string}> {
	let result = {url: "", fileName: ""};
	await graphQLServices.documentsClient.query({
		query: GET_PRESIGNED_URL,
		variables: {
			fileId,
			documentId,
			text
		},
		fetchPolicy: "network-only",
	})
		.then(value => result = value.data.filePresignedUrl)
		.catch(_ => result = {url: "", fileName: ""});
	return result;
}


function DocumentFx() {
	const document = useRef<IDocument>();
	const [documentIsLocked, setDocumentIsLocked] = useState<boolean>(false);
	
	const [documentLockError, setDocumentLockError] = useState<string>();
	const [documentIsValid, setDocumentIsValid] = useState<boolean>(false);
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);
	const [showLogs, setShowLogs] = useState<boolean>(false);
	const [showAudits, setShowAudits] = useState<boolean>(false);
	const {documentId } = useParams();
	const [refreshId, setRefreshId ]= useState<number>(0);
	const [mailDocument, setMailDocument] = useState<mailDocumentType|undefined>();
	const [splitDocument, setSplitDocument] = useState<boolean>(false);
	const [editComment, setEditComment] = useState<boolean>(false);
	const [reindex, setReindex] = useState<boolean>(false);
	const navigate = useNavigate();
	const [navigateTo, setNavigateTo] = useState<string>();
	const [currentFile, setCurrentFile] = useState<number>();
	
	const documentViewerRef = useRef<IDocumentViewerHandles>(null);
	
	const fetch = async (id: number) => {
		setSubmitLoading(true);
		if (!subscription || subscription.error) {
			setRefreshId(refreshId + 1);
		}
		graphQLServices.documentsClient.query({
			query: GET_DOCUMENTS_BY_ID,
			variables: {
				id: id,
			},
		}).then((result) => {
			document.current = result?.data?.documents?.nodes[0];

			setDocumentIsValid(((document.current?.validations?.filter((it) => it.severity === ValidationSeverity.ERROR && it.field).length)??0) === 0);
			setDocumentLockError(undefined);

		}).finally(() => {
			setSubmitLoading(false);
		});
	};

	useEffect(() => {
		if (documentId) fetch(+documentId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentId]);

	useEffect(() => {
		setDocumentIsLocked(documentLockError === undefined);
	},[documentLockError]);

	function getSubscriptionVariables(documentId: string|undefined) {
		return {id: parseInt(documentId??"0")};
	}
	const readOnly = document.current?.status !== DocumentStatus.Manual || !documentIsLocked;

	const subscription  = useSubscription(LOCK_DOCUMENT, {
		client : graphQLServices.documentsClient,
		variables: getSubscriptionVariables(documentId),
		shouldResubscribe: true,
		skip: readOnly || !documentId ,
		onError: ((error) => {
			setDocumentLockError(error.message);
		}),
		onData: _ => {
			setDocumentLockError(undefined);
		}
		
	});

	useSubscription(SUBSCRIBE_SAVEMETADATA, {
		client : graphQLServices.documentsClient,
		variables: getSubscriptionVariables(documentId),
		fetchPolicy: "no-cache",
		shouldResubscribe: true,
		skip: !readOnly || !documentId,
		onData: data => {
			console.info(JSON.stringify(data.data));
			toast.warning("Document has been changed " + JSON.stringify(data));
		} ,
	});
	
	
	function onMetaDataChanged(newMetaData: DocumentMeta[]|null) {
		if (document.current) {
			document.current.metaData = newMetaData;
		}
	}

	function onValidationDataChanged(validationData: DocumentValidation[]|null) {
		if (document.current) {
			document.current.validations = validationData;
			setDocumentIsValid(((document.current?.validations?.filter((it) => it.severity === ValidationSeverity.ERROR && it.field).length)??0) === 0);
		}

	}

	function onAuditDataChanged(auditData: DocumentAudit[]|null) {
		if(document.current) {
			if (!document.current.audits) {
				document.current.audits = [...auditData??[]];
			} else {
				document.current.audits = Object.assign([], document.current?.audits);
				document.current.audits.push(...(auditData??[]).filter((x) => x.id === 0));
			}
			
		}

	}
	
	const changeFile = (newDocumentId: number) => {
		documentViewerRef.current?.selectDocument(newDocumentId);
	};
	
	
	if (navigateTo) {
		const nav = navigateTo;
		setNavigateTo(undefined);
		navigate(nav, {replace: true});
		navigate(0);
		return <></>;
	}

	if (submitLoading) return <CircularProgress/>;

	if (subscription.loading) return <CircularProgress/>;
	if (!document.current) return <Alert severity={"warning"}>{translate("Document not found.")}</Alert>;
	
	return (
		<BreadcrumbsWrapper endAdornment={
			<Tooltip id="button-refresh" title={translate("Refresh")}>
				<IconButton onClick={() => document.current?.id && fetch(document.current?.id)}>
					<Refresh/>
				</IconButton>
			</Tooltip>
		}>
			{documentLockError &&
				<Alert severity={"warning"}>{translate(documentLockError)}</Alert>
			}
			{editComment &&
				<GenericModal open={editComment} title={translate("Comment")} onClose={() => setEditComment(false)}>
					<EditCommentCard readOnly={readOnly} document={document.current}
						reloadDocument={() => fetch(document.current?.id ?? 0)} onClose={() => setEditComment(false)}/>
				</GenericModal>
			}
			
			{mailDocument !== undefined && <GenericModal
				open={true} title={translate("Mail Document")}
				confirmClose={true}
				confirmTitle={translate("Close form")}
				confirmSuccessTitle={translate("Close")}
				confirmCancelTitle={translate("Cancel")}
				onClose={() => {
					setMailDocument(undefined);
				}}>
				<MailDocumentCard mailDocumentType={mailDocument} document={document.current}
					reloadDocument={() => fetch(document.current?.id ?? 0)} onClose={() => setMailDocument(undefined)}/>
			</GenericModal>
			}
			{splitDocument && <GenericModal open={splitDocument} title={translate("Split Document")}
				onClose={() => setSplitDocument(false)}>
				<SplitDocumentCard document={document.current} reloadDocument={() => fetch(document.current?.id ?? 0)}
					onClose={() => setSplitDocument(false)}/>
			</GenericModal>}
			{reindex && <GenericModal open={true} title={translate("Reindex Document")} onClose={() => {
				fetch(document.current?.id ?? 0);
				setReindex(false);
			}
			}>
				<ReindexDocumentCard
					navigateToId={(id) => {
						setNavigateTo("../" + id);
					}}
					documentIsValid={documentIsValid}
					document={document.current}
					reloadDocument={() => fetch(document.current?.id ?? 0)}
					onClose={() => {
						fetch(document.current?.id ?? 0);
						setReindex(false);
					}
					}/>
			</GenericModal>
			}
			<div style={{height: "100%"}}>
				<Box style={topContainerStyling}>
					<div>
						<DocumentActionsCard
							readOnly={readOnly}
							document={document.current}
							documentIsValid={documentIsValid}
							reloadDocument={() => fetch(document.current?.id ?? 0)}
							mailDocument={(mailDocumentType) => setMailDocument(mailDocumentType)}
							splitDocument={() => setSplitDocument(true)}
							editComment={() => setEditComment(true)}
							reindex={() => setReindex(true)}
							locked={documentIsLocked}
						/>
					</div>
					<div style={{flexGrow: 1}}></div>
					<Chip label={document.current.status}/>
					
					{document.current?.source && <Chip label={document.current.source.description}/>}
					<Button  onClick={() => setShowAudits(true)}>Audits</Button>
					<Button  onClick={() => setShowLogs(true)}>Logs</Button>
				</Box>
				<Box style={containerStyling}>
					<Box sx={metaDataContainerStyling}>
						{document.current?.deleted &&
							<Alert severity={"warning"}>{translate("Document is deleted")}</Alert>}
						{((document.current?.comment ?? "") !== "") &&
							<Alert
								sx={{cursor: "pointer", marginTop: "5px"}}
								icon={<Comment/>}
								color={"info"}
								style={{whiteSpace: "pre-line"}}
								onClick={() => setEditComment(true)}
							>{document.current?.comment}</Alert>}
						<MetaCard
							readOnly={readOnly}
							documentId={document.current?.id ?? 0}
							metaData={document.current?.metaData ?? []}
							onMetaDataChanged={onMetaDataChanged}
							onValidationsChanged={onValidationDataChanged}
							onAuditsChanged={onAuditDataChanged}
							validations={document.current?.validations ?? []}
							files={document.current?.files ??[]}
							setCurrentFile={changeFile}
							currentFile={currentFile}
						/>
					</Box>
					<Box style={documentViewerContainerStyling}>
						<DocumentViewer
							ref={documentViewerRef}
							documentId={document.current?.id ?? 0}
							groupId={0}
							onSelectedFileChanged={(currentFile) => setCurrentFile(currentFile)}
							defaultDocumentId={document.current?.files?.filter(f => f.type !== DocumentFileType.DocumentSource)?.first().id}
							sourceDocument={(document.current?.files ?? []).filter(f => f.type === DocumentFileType.DocumentSource).map((it: DocumentFile) => {
								return {
									extension: it.extension ?? "",
									name: it.name ?? "",
									id: it.id,
									getPresignedUrl: async () =>
										await getPresignedUrl(it.id, +(document.current?.id ?? 0), false),
									groupId: it.groupId,
									parentGroupId: it.parentGroupId
								};
							}
							).firstOrNull() ?? undefined}
							documents={(document.current?.files?.filter(f => f.type !== DocumentFileType.DocumentSource) ?? [])
								.map((it) => {
									return {
										extension: it.extension ?? "",
										name: it.name ?? "",
										id: it.id,
										getPresignedUrl: async () =>
											await getPresignedUrl(it.id, +(document.current?.id ?? 0), it.extension === ".msg" || it.extension === ".eml"),
										groupId: it.groupId,
										parentGroupId: it.parentGroupId
									};
								}) ?? []
							}
						>
							<DocumentCard document={document.current}/>
						</DocumentViewer>
					</Box>
				</Box>
			</div>
			<GenericModal open={showAudits} title={translate("Audits")} onClose={() => setShowAudits(false)}>
				<AuditCard audits={document.current?.audits??[]}/>
			</GenericModal>
			<GenericModal open={showLogs} title={translate("Logs")} onClose={() => setShowLogs(false)}>
				<LogCard logs={document.current?.logs ?? []}/>
			</GenericModal>
		</BreadcrumbsWrapper>
	
	
	);
}

export const Document = pageAuthorizedByGroup(DocumentFx, ["Admin", "Documents"]);
